import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { SlidersHorizontal } from "lucide-react";

const WEIGHT_FIELDS = [
    { key: "velocity", label: "Velocity", desc: "Stage moves in the last 14 days per active candidate" },
    { key: "aging", label: "Stage aging", desc: "How long candidates sit in their current stage" },
    { key: "activity", label: "Activity recency", desc: "Days since the last touch on any candidate" },
    { key: "target", label: "Target progress", desc: "Hires made vs target (or openings)" },
    { key: "sla", label: "SLA runway", desc: "Days open vs the job's SLA (skipped when no SLA set)" },
];
const DEFAULTS = { velocity: 25, aging: 20, activity: 15, target: 25, sla: 15 };

// Customize health-score weightings + the company-wide default SLA.
export default function HealthWeightsDialog({ open, onClose, config, onSaved }) {
    const [w, setW] = useState(DEFAULTS);
    const [sla, setSla] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setW({ ...DEFAULTS, ...(config?.health_weights || {}) });
        setSla(config?.default_sla_days ? String(config.default_sla_days) : "");
    }, [open, config]);

    const total = WEIGHT_FIELDS.reduce((s, f) => s + (Number(w[f.key]) || 0), 0);

    const save = async () => {
        setSaving(true);
        try {
            const payload = { health_weights: Object.fromEntries(WEIGHT_FIELDS.map((f) => [f.key, Number(w[f.key]) || 0])) };
            if (sla === "") payload.clear_default_sla = true;
            else payload.default_sla_days = Number(sla);
            const { data } = await api.put("/company/pipeline-config", payload);
            toast.success("Pipeline settings saved");
            onSaved?.(data);
            onClose?.();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
            <DialogContent className="max-w-lg" data-testid="weights-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2">
                        <SlidersHorizontal className="w-4 h-4 text-ikb" /> Health score weights
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-3">
                    {WEIGHT_FIELDS.map((f) => (
                        <div key={f.key} className="flex items-center gap-3" data-testid={`weight-row-${f.key}`}>
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium">{f.label}</div>
                                <div className="text-[11px] text-foreground/60">{f.desc}</div>
                            </div>
                            <input
                                type="number"
                                min="0"
                                max="100"
                                value={w[f.key]}
                                onChange={(e) => setW((cur) => ({ ...cur, [f.key]: e.target.value }))}
                                className="w-20 h-9 border border-border px-2 text-sm text-right bg-white outline-none focus:border-ikb"
                                data-testid={`weight-input-${f.key}`}
                            />
                        </div>
                    ))}
                    <div className={`text-[11px] font-mono uppercase tracking-widest text-right ${total === 100 ? "text-emerald-700" : "text-amber-700"}`} data-testid="weights-total">
                        Total: {total} {total !== 100 && "(auto-normalized)"}
                    </div>
                </div>

                <div className="border-t border-border pt-4">
                    <label className="block max-w-xs">
                        <span className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">Company default SLA (days to fill)</span>
                        <input
                            type="number"
                            min="1"
                            max="365"
                            value={sla}
                            onChange={(e) => setSla(e.target.value)}
                            placeholder="No default SLA"
                            className="mt-1 w-full h-9 border border-border px-2 text-sm bg-white outline-none focus:border-ikb"
                            data-testid="default-sla-input"
                        />
                    </label>
                </div>

                <DialogFooter className="gap-2">
                    <Button variant="ghost" onClick={() => { setW(DEFAULTS); }} data-testid="weights-reset">Reset defaults</Button>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={save} disabled={saving} className="bg-ikb hover:bg-[#001f7a]" data-testid="weights-dialog-save">
                        {saving ? "Saving…" : "Save"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
